function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function showBootError(message: string) {
  window.__mszBootFailed = true;
  window.__mszBootPhase = "mountApp:showBootError";
  const rootElement = document.getElementById("root");
  if (!rootElement) return;
  rootElement.innerHTML = `
    <div style="min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:1.5rem;font-family:system-ui,sans-serif;background:rgb(254, 242, 242);color:rgb(153, 27, 27);text-align:center;max-width:28rem;margin:0 auto">
      <p style="font-weight:600;margin:0 0 0.5rem">MadSportz could not start</p>
      <p style="margin:0;font-size:0.875rem;line-height:1.5;word-break:break-word">${escapeHtml(message)}</p>
      <p style="margin:1rem 0 0;font-size:0.8rem;color:rgb(100, 116, 139)">Try: disable ad blockers for this site, use mobile data or another network, open a private window, or another browser (Chrome / Safari).</p>
    </div>
  `;
}

function showRenderCrash(err: unknown): void {
  window.__mszBootFailed = true;
  window.__mszBootPhase = "mountApp:renderCrash";
  const text =
    err instanceof Error
      ? `${err.message}\n\n${err.stack ?? ""}`
      : typeof err === "string"
        ? err
        : String(err);
  console.error("[MadSportz.boot] BOOT FAILURE at createRoot().render():", err);
  const rootElement = document.getElementById("root");
  if (!rootElement) return;
  rootElement.innerHTML = `
    <div style="min-height:100vh;padding:1.5rem;font-family:system-ui,monospace;background:rgb(254, 242, 242);color:rgb(153, 27, 27);max-width:42rem;margin:0 auto">
      <h1 style="font-size:1.25rem;margin:0 0 0.75rem">App crashed during React render</h1>
      <pre style="white-space:pre-wrap;word-break:break-word;font-size:0.8rem;text-align:left;margin:0">${escapeHtml(text)}</pre>
      <p style="margin-top:1rem;font-size:0.8rem;color:rgb(100, 116, 139)">Open DevTools → Console for full logs. Try <code>?msz_boot_shell=1</code> to test JS without loading App.</p>
    </div>
  `;
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const id = window.setTimeout(() => {
      reject(new Error(`${label} timed out after ${ms / 1000}s (slow network or blocked request).`));
    }, ms);
    promise.then(
      (v) => {
        window.clearTimeout(id);
        resolve(v);
      },
      (e) => {
        window.clearTimeout(id);
        reject(e);
      },
    );
  });
}

function validateEnvironment(): void {
  const required = [
    ['VITE_SUPABASE_URL', import.meta.env.VITE_SUPABASE_URL],
    ['VITE_SUPABASE_ANON_KEY', import.meta.env.VITE_SUPABASE_ANON_KEY],
  ] as const;
  const missing = required.filter(([, val]) => !val).map(([key]) => key);
  if (missing.length > 0) {
    throw new Error(
      `Missing required environment variables: ${missing.join(', ')}. ` +
      'Copy .env.example to .env.local and fill in the values.'
    );
  }
}

export async function mountApp(): Promise<void> {
  window.__mszBootPhase = "mountApp:start";

  validateEnvironment();

  const { readBootFlags } = await import("./boot-flags");
  const flags = readBootFlags();

  const rootElement = document.getElementById("root");
  if (!rootElement) {
    throw new Error("Root element #root not found");
  }

  try {
    if (flags.debug) {
      const { logImportMetaEnvDiagnostics } = await import("./boot-env-log");
      logImportMetaEnvDiagnostics();
    }

    // Diagnostic: only react + react-dom — proves bundles execute; skips App, ThemeProvider, SW-heavy tree.
    if (flags.shell) {
      window.__mszBootPhase = "mountApp:shell:import-chunks";
      console.info("[MadSportz.boot] msz_boot_shell=1 → loading react + react-dom only");
      const [React, reactDomClient] = await Promise.all([
        withTimeout(import("react"), 60_000, "react bundle"),
        withTimeout(import("react-dom/client"), 60_000, "react-dom bundle"),
      ]);
      const { createRoot } = reactDomClient;
      const { createElement } = React;
      try {
        createRoot(rootElement).render(
          createElement(
            "div",
            {
              style: {
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "system-ui,sans-serif",
                padding: "1.5rem",
                textAlign: "center",
              },
            },
            createElement("p", { style: { margin: 0, fontWeight: 600 } }, "MadSportz: JS shell OK"),
            createElement(
              "p",
              { style: { margin: "0.5rem 0 0", fontSize: "0.875rem", color: "rgb(100, 116, 139)" } },
              "React + DOM loaded; App bundle was not imported. Remove ?msz_boot_shell=1 for full app.",
            ),
          ),
        );
        window.__mszBootPhase = "mountApp:shell:ready";
        window.__mszReady = true;
        console.info("[MadSportz.boot] Shell render complete");
      } catch (renderErr) {
        showRenderCrash(renderErr);
      }
      return;
    }

    window.__mszBootPhase = "mountApp:import(core+app)";
    console.info("[MadSportz.boot] STEP 4: loading react, react-dom, next-themes, App (parallel)…");
    // Load entry chunks in parallel so slow networks don’t hit the 55s “Still loading” watchdog
    // while only 1 of 4 bundles has finished (sequential was up to ~225s before any React render).
    const [React, reactDomClient, nextThemes, appModule] = await Promise.all([
      withTimeout(import("react"), 60_000, "react bundle"),
      withTimeout(import("react-dom/client"), 60_000, "react-dom bundle"),
      withTimeout(import("next-themes"), 60_000, "next-themes bundle"),
      withTimeout(import("./App.tsx"), 120_000, "App bundle"),
    ]);
    const { createRoot } = reactDomClient;
    const { ThemeProvider } = nextThemes;
    const { default: App } = appModule;

    const { createElement } = React;

    void import("@/lib/tracking").then((tracking) => {
      tracking.initTracking({ platform: "web" });
      tracking.installGlobalErrorTracking();
    }).catch((e: unknown) => {
      console.warn("[MadSportz.boot] Tracking init failed:", e);
    });

    window.__mszBootPhase = "mountApp:render";
    console.info("[MadSportz.boot] STEP 5: createRoot().render(ThemeProvider > App)…");
    try {
      createRoot(rootElement).render(
        createElement(ThemeProvider, {
          attribute: "class",
          defaultTheme: "light",
          enableSystem: true,
          disableTransitionOnChange: true,
          children: createElement(App),
        }),
      );
      window.__mszBootPhase = "mountApp:ready";
      window.__mszReady = true;
      console.info("[MadSportz.boot] STEP 6: initial render scheduled (__mszReady=true)");
    } catch (renderErr) {
      // createRoot().render() does not throw on component errors in React 18.
      // Component errors are caught by AppErrorBoundary inside App.tsx.
      showRenderCrash(renderErr);
    }
  } catch (err) {
    console.error("[MadSportz.boot] load/mount failed:", err);
    const msg =
      err instanceof Error
        ? err.message
        : typeof err === "string"
          ? err
          : "Failed to load the application.";
    showBootError(msg);
  }
}
